export async function extractLastResult(stream) {
  try {
    const text = await readStreamToString(stream);

    // Try to parse as JSON array first
    let parsedData;
    try {
      parsedData = safeJsonParse(text);
    } catch (parseErr) {
      // Fallback: Split into separate JSON objects for legacy format
      const chunks = text
        .trim()
        .split(/}\s*{/)
        .map((chunk, i, arr) => {
          if (i === 0) return chunk + "}";
          if (i === arr.length - 1) return "{" + chunk;
          return "{" + chunk + "}";
        });

      // Parse last JSON
      parsedData = safeJsonParse(chunks[chunks.length - 1]);
    }

    // Handle array response format
    let last;
    if (Array.isArray(parsedData)) {
      last = parsedData[parsedData.length - 1];
    } else {
      last = parsedData;
    }

    const cleaned = stripMarkdown(
      last?.content?.parts?.[0]?.function_response?.response?.result,
    );
    const tsMs = Number.isFinite(last?.timestamp)
      ? Math.round(last?.timestamp * 1000)
      : Date.now();

    return { text: cleaned, timestampMs: tsMs };
  } catch (err) {
    console.error("Failed to extract result:", err);
    return null;
  }
}

export async function extractLastResultStream(stream) {
  // query-stream
  const allowedAuthors = new Set([
    "intent_classifier_agent",
    "general_golf_knowledge_agent",
    "introduction_tool",
    // "analysis_manager_agent",
    "report_analyser",
    "summarizer",
    "report_agent",
  ]);

  const pickTextFromItem = (item) => {
    const raw = item?.content?.parts?.[0]?.text;
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    return trimmed.length ? raw : null;
  };

  try {
    const text = await readStreamToString(stream);

    // Try to parse as JSON (array or object) first
    let parsedData;
    try {
      parsedData = safeJsonParse(text);
    } catch (parseErr) {
      // Fallback: Split into separate JSON objects for legacy format
      const chunks = text
        .trim()
        .split(/}\s*{/)
        .map((chunk, i, arr) => {
          if (i === 0) return chunk + "}";
          if (i === arr.length - 1) return "{" + chunk;
          return "{" + chunk + "}";
        });

      // Parse last JSON chunk (legacy stream behavior)
      parsedData = safeJsonParse(chunks[chunks.length - 1]);
    }

    const items = Array.isArray(parsedData) ? parsedData : [parsedData];

    // Find last eligible author with non-empty text by scanning backwards
    let chosen = null;
    let chosenText = null;

    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      // if (!allowedAuthors.has(item?.author)) continue;

      const candidate = pickTextFromItem(item);
      if (candidate) {
        chosen = item;
        chosenText = candidate;
        break;
      }
      // if text is empty, continue scanning for next eligible author
    }

    // If nothing matched (or all matched had empty text), you can decide:
    // - return null (strict)
    // - or fall back to previous behavior (lenient)
    if (!chosen) return null;

    const cleaned = stripMarkdown(chosenText);

    const tsMs = Number.isFinite(chosen?.timestamp)
      ? Math.round(chosen.timestamp * 1000)
      : Date.now();

    return { text: cleaned, timestampMs: tsMs };
  } catch (err) {
    console.error("Failed to extract result:", err);
    return null;
  }
}

export async function extractResult(stream) {
  //query
  try {
    const text = await readStreamToString(stream);
    let parsed = safeJsonParse(text);

    // Handle array response format
    if (Array.isArray(parsed)) {
      parsed = parsed[parsed.length - 1];
    }

    const cleaned = stripMarkdown(parsed?.content?.parts?.[0]?.text);
    const tsMs = Number.isFinite(parsed?.timestamp)
      ? Math.round(parsed?.timestamp * 1000)
      : Date.now();

    return { text: cleaned, timestampMs: tsMs };
  } catch (err) {
    console.error("Failed to extract result:", err);
    return null;
  }
}

export async function readStreamToString(stream) {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let text = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    text += decoder.decode(value, { stream: true });
  }
  text += decoder.decode(); // flush
  return text;
}

const RAW_DATA_PREFIXES = ["SwingScoreReport(", "SwingReport("];

export function formatUserMessage(rawText, parts = []) {
  const isRawData = RAW_DATA_PREFIXES.some((prefix) =>
    rawText.trimStart().startsWith(prefix),
  );

  if (!isRawData) return rawText;

  const fileNames = parts
    .map(
      (p) =>
        p?.fileData?.displayName ??
        p?.file_data?.display_name ??
        // Defensive fallback in case the backend nests differently
        p?.file_data?.displayName
    )
    .filter(Boolean);

  return fileNames.length
    ? `Submitted swing analysis: ${fileNames.join(", ")}`
    : "Submitted swing analysis";
}

export function stripMarkdown(raw = "") {
  if (!raw.includes("|")) return raw;

  // 1) Normalize escaped newlines if any were sent as \n literals
  const text = raw.replace(/\\n/g, "\n");

  // 2) If the text already contains GFM alignment rows (e.g. | :--- | or | --- |),
  //    it's properly formatted markdown — return as-is so multi-table responses
  //    render correctly without the table reconstruction logic corrupting them.
  if (/\|[ \t]*:?-{3,}:?[ \t]*\|/.test(text)) {
    return text;
  }

  // 4) Find the FIRST table-like chunk: from first '|' to last '|'
  const firstPipe = text.indexOf("|");
  const lastPipe = text.lastIndexOf("|");
  if (firstPipe === -1 || lastPipe === -1 || lastPipe <= firstPipe) return text;

  const prefix = text.slice(0, firstPipe).trimEnd();
  const tableChunk = text.slice(firstPipe, lastPipe + 1);
  const suffix = text.slice(lastPipe + 1).trimStart();

  // 5) Split into trimmed cells
  const cells = tableChunk
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean);

  if (cells.length < 2) return text;

  // 6) Detect alignment row (e.g. :---, ---:, :---:, ---)
  const isAlignCell = (c) => /^:?-{3,}:?$/.test(c);

  // Try to detect column count from a contiguous alignment row
  let colCount = 0;
  for (let i = 0; i < cells.length; i++) {
    // scan forward for a run of align cells
    let j = i;
    while (j < cells.length && isAlignCell(cells[j])) j++;
    const run = j - i;
    if (run >= 2) {
      // assume this run indicates the alignment row length
      colCount = run;
      break;
    }
  }
  if (!colCount) colCount = 3; // fallback (your current case)

  // 7) Group into rows by colCount
  const rows = [];
  for (let i = 0; i < cells.length; i += colCount) {
    rows.push(cells.slice(i, i + colCount));
  }
  if (rows.length < 1) return text;

  // 8) Header + alignment handling
  const header = rows[0];
  let bodyRows = rows.slice(1);

  // If the first body row is an alignment row, use it; otherwise synthesize one
  const isAlignRow = (r) => r.length === colCount && r.every(isAlignCell);

  let alignRow;
  if (bodyRows.length && isAlignRow(bodyRows[0])) {
    alignRow = bodyRows[0];
    bodyRows = bodyRows.slice(1); // drop alignment from body
  } else {
    alignRow = Array.from({ length: header.length }, () => ":---");
  }

  // 9) Build Markdown table
  const mdTable =
    `| ${header.join(" | ")} |\n` +
    `| ${alignRow.join(" | ")} |\n` +
    bodyRows.map((r) => `| ${r.join(" | ")} |`).join("\n");

  // 10) Reassemble with blank lines around table for markdown rendering
  return `${prefix}\n\n${mdTable}\n\n${suffix}`;
}

// Helper function to safely parse JSON with better error handling
function safeJsonParse(text) {
  try {
    return JSON.parse(text.trim());
  } catch (error) {
    console.warn("Initial JSON parse failed, attempting recovery:", {
      error: error.message,
      textEnd: "..." + text.trim().substring(text.length - 200),
      textLength: text.length,
      textPreview: text.trim().substring(0, 200) + "...",
    });

    // Try to find the first complete JSON object/array if partial data
    const trimmed = text.trim();

    // Look for array format
    if (trimmed.startsWith("[")) {
      const lastBracket = trimmed.lastIndexOf("]");
      if (lastBracket > 0) {
        try {
          const recovered = trimmed.substring(0, lastBracket + 1);
          console.log(
            "Successfully recovered JSON array:",
            recovered.length,
            "characters",
          );
          return JSON.parse(recovered);
        } catch (error) {
          console.warn("Array recovery failed:", error.message);
        }
      }
    }

    // Look for object format
    if (trimmed.startsWith("{")) {
      const lastBrace = trimmed.lastIndexOf("}");
      if (lastBrace > 0) {
        try {
          const recovered = trimmed.substring(0, lastBrace + 1);
          console.log(
            "Successfully recovered JSON object:",
            recovered.length,
            "characters",
          );
          return JSON.parse(recovered);
        } catch (error) {
          console.warn("Object recovery failed:", error.message);
        }
      }
    }

    console.error("JSON parsing completely failed, throwing original error");
    throw error;
  }
}
